import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CocktailIcon } from '@/components/Brand';
import { InstallButton } from '@/components/InstallButton';
import { toast } from 'sonner';

export default function SignupPage() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!username.trim() || username.trim().length < 2) {
      setError('Pick a username (2+ chars).'); return;
    }
    if (password.length < 4) { setError('Password must be at least 4 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    const res = await signup(username.trim(), password);
    setLoading(false);
    if (res.ok) {
      toast.success('Account created.');
      navigate('/dashboard');
    } else {
      setError(res.error || 'Signup failed');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative">
      <div
        className="absolute inset-0 opacity-15 pointer-events-none"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1727408407916-459954e4671a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black pointer-events-none" />
      <div className="grain-overlay" />

      <div className="relative z-10 w-full max-w-md animate-fade-up">
        <div className="flex items-center gap-3 mb-12">
          <CocktailIcon className="text-[#D4AF37]" size={32} />
          <div>
            <div className="font-display text-2xl font-medium tracking-tight">kahrency</div>
            <div className="label-eyebrow">The bartenders' ledger</div>
          </div>
        </div>

        <h1 className="text-4xl sm:text-5xl font-display font-medium tracking-tighter mb-2">Open a tab.</h1>
        <p className="text-slate-400 mb-10">Set up your account in seconds — no email required.</p>

        <form onSubmit={submit} className="space-y-6" data-testid="signup-form">
          <div>
            <Label className="label-eyebrow mb-2 block">Username</Label>
            <Input
              data-testid="signup-username-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="bg-black border-white/15 text-white h-12 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
              placeholder="choose a handle"
            />
          </div>
          <div>
            <Label className="label-eyebrow mb-2 block">Password</Label>
            <Input
              data-testid="signup-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-black border-white/15 text-white h-12 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
              placeholder="••••••••"
            />
          </div>
          <div>
            <Label className="label-eyebrow mb-2 block">Confirm password</Label>
            <Input
              data-testid="signup-confirm-input"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="bg-black border-white/15 text-white h-12 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
              placeholder="••••••••"
            />
          </div>
          {error && (
            <div data-testid="signup-error" className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 rounded px-3 py-2">
              {error}
            </div>
          )}
          <Button
            type="submit"
            data-testid="signup-submit-button"
            disabled={loading}
            className="w-full h-12 gold-btn"
          >
            {loading ? 'Creating…' : 'Create account'}
          </Button>
        </form>

        <div className="mt-10 pt-6 border-t border-white/5 text-sm text-slate-400 flex items-center justify-between gap-4 flex-wrap">
          <div>
            Already have an account?{' '}
            <Link to="/login" data-testid="signup-to-login-link" className="text-[#D4AF37] hover:text-[#FDE047]">
              Sign in
            </Link>
          </div>
          <InstallButton />
        </div>
      </div>
    </div>
  );
}
